// frontend/src/components/Layout.jsx - v2
import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, Box, IconButton, Avatar, Divider, Tooltip,
  useMediaQuery, useTheme, Badge
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  NotificationsActive as FollowUpIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Assignment as LeadIcon,
  ViewKanban as PipelineIcon,
  Description as QuotationIcon,
  Storefront as VendorIcon,
  Inventory2 as CatalogIcon,
  RequestQuote as EnquiryIcon,
  FactCheck as ProcurementIcon,
  Task as TaskIcon,
  BarChart as ReportIcon,
  Logout as LogoutIcon,
  Phone as PhoneIcon,
  Settings as SettingsIcon,
  AccountBalance as BranchIcon,
  AdminPanelSettings as PlatformIcon,
  Assignment as SalesOrderIcon,
  LocalShipping as PoIcon,
  ReceiptLong as InvoiceIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import ChatBubble from './ChatBubble';
import TrialBanner from './TrialBanner';
// import tackleLogo from '../images/tackleprime-logo.png';
import followUpAPI from '../api/followUpAPI';
import settingsAPI from '../api/settingsAPI';
import companyAPI from '../api/companyAPI';
import { resolveLogoUrl } from '../utils/companyBranding';
import pkg from '../../package.json';

const drawerWidth = 240;

// module key = matches the key in department_module_permissions.
// Items with no module key are always visible (Dashboard, Users, Departments, Settings).
const menuItems = [
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  // Deliberately second, and deliberately NOT module-gated: it gathers work
  // from leads, quotations, call logs and tasks, so gating it behind any one
  // module would hide the others' follow-ups from someone who has them.
  { text: 'Follow-ups',    icon: <FollowUpIcon />,  path: '/follow-ups' },
  // Setup order mirrors how an organization actually onboards: departments and
  // branches exist first, then users get slotted into them by role.
  { text: 'Departments',   icon: <BusinessIcon />,  path: '/departments',   roles: ['admin'] },
  { text: 'Branches',      icon: <BranchIcon />,    path: '/branches',      roles: ['admin'] },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/users',         roles: ['admin'] },
  { text: 'Call Logs',     icon: <PhoneIcon />,     path: '/call-logs',     module: 'call_logs' },
  { text: 'Leads',         icon: <LeadIcon />,      path: '/leads',         module: 'leads' },
  { text: 'Pipeline',      icon: <PipelineIcon />,  path: '/pipeline',      module: 'leads' },
  // Enquiries sits between leads and vendor pricing because that is where it
  // sits in the work: the customer has asked, and we do not have prices yet.
  { text: 'Enquiries',     icon: <EnquiryIcon />,   path: '/enquiries',     module: 'enquiries' },
  { text: 'Procurement',   icon: <ProcurementIcon />, path: '/procurement', module: 'procurement' },
  { text: 'Vendors',       icon: <VendorIcon />,    path: '/vendors',       module: 'vendors' },
  // Vendor Prices is deliberately NOT in the sidebar. It no longer records
  // anything — prices are entered on the enquiry, next to the line the customer
  // asked for — so as a nav item it advertised a second way to do one job. The
  // page still exists and is still needed: it is the searchable, exportable
  // price history, and the only place a mistyped price can be corrected or
  // deleted. Reached from Vendor Catalog, one click from what it explains.
  { text: 'Vendor Catalog',icon: <CatalogIcon />,   path: '/vendor-catalog',module: 'vendor_catalog' },
  { text: 'Quotations',    icon: <QuotationIcon />, path: '/quotations',    module: 'quotations' },
  // Order of the actual sales flow: quote → customer confirms (sales order)
  // → we buy (vendor PO) → we bill (invoice).
  { text: 'Sales Orders',  icon: <SalesOrderIcon />, path: '/sales-orders',  module: 'sales_orders' },
  { text: 'Purchase Orders', icon: <PoIcon />,      path: '/purchase-orders', module: 'purchase_orders' },
  { text: 'Invoices',      icon: <InvoiceIcon />,   path: '/invoices',      module: 'invoices' },
  { text: 'Tasks',         icon: <TaskIcon />,      path: '/tasks',         module: 'tasks' },
  { text: 'Reports',       icon: <ReportIcon />,    path: '/reports',       module: 'reports', roles: ['admin', 'manager'] },
  { text: 'Settings',      icon: <SettingsIcon />,  path: '/settings',      roles: ['admin'] },
  { text: 'Platform',      icon: <PlatformIcon />,  path: '/platform',      roles: ['platform_superadmin'] }
];

const initials = (name) =>
  (name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();

const POLL_MS = 60_000; // refresh badge every 60 seconds

export default function Layout({ user, onLogout, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [overdueFollowUps, setOverdueFollowUps] = useState(0);
  // null = not loaded yet, array = list of allowed module keys for this user
  const [allowedModules, setAllowedModules] = useState(null);
  const [company, setCompany] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch allowed modules for this user (once on mount). For admins this is
  // the company's plan-available modules (department restrictions don't
  // apply to admins, but the platform-level plan gate still does — an admin
  // on the basic plan must not see Invoices/Reports/etc in the sidebar).
  useEffect(() => {
    settingsAPI.getMyModules()
      .then(data => setAllowedModules(data.modules))
      .catch(() => setAllowedModules(null)); // on error, show all (fail open)
  }, []);

  // Fetch the company's own profile for the sidebar logo/name — a platform
  // superadmin has no company, so this simply stays null for them (fine,
  // they get the generic Narevek mark instead).
  useEffect(() => {
    if (user?.role === 'platform_superadmin') return;
    companyAPI.getProfile()
      .then(setCompany)
      .catch(() => setCompany(null));
  }, [user]);

  // Poll the unified follow-up queue for the sidebar badge — overdue + due
  // today, the two buckets that need action now. Upcoming and stale stay off
  // the badge deliberately: stale is backlog, not a fire to put out today.
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const sum = await followUpAPI.summary();
        setOverdueFollowUps((sum?.overdue || 0) + (sum?.today || 0));
      } catch {
        // silently ignore — badge just stays at last known value
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(prev => !prev);
  const handleNavigation = (path) => { navigate(path); setMobileOpen(false); };
  const handleLogout = () => { onLogout(); navigate('/login'); };

  // A platform superadmin belongs to no company and manages entitlements
  // across tenants — they should only ever see the Platform item, never any
  // business-data screen (which wouldn't work for them anyway: no company_id).
  const filteredMenuItems = user?.role === 'platform_superadmin'
    ? menuItems.filter(item => item.roles?.includes('platform_superadmin'))
    : menuItems.filter(item => {
        // Role check — if item has a roles restriction, user must match
        if (item.roles && !item.roles.includes(user?.role)) return false;
        // Module check — admin always sees all; null allowedModules = all enabled
        if (item.module && allowedModules != null && !allowedModules.includes(item.module)) return false;
        return true;
      });

  const isSelected = (path) =>
    path === '/dashboard'
      ? location.pathname === '/' || location.pathname === '/dashboard'
      : location.pathname.startsWith(path);

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: '#e2e8f0'
    }}>
      {/* Logo / Brand */}
<Box sx={{
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 1.5,
  py: 1.5,
  borderBottom: '1px solid rgba(255,255,255,0.07)'
}}>
  <Box sx={{
    bgcolor: '#ffffff',
    borderRadius: 2,
    px: 1.5,
    py: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  }}>

    {resolveLogoUrl(company?.logo_url) ? (
      <img
        src={resolveLogoUrl(company.logo_url)}
        alt={company?.name || 'Company logo'}
        style={{
          width: '160px',
          maxWidth: '100%',
          maxHeight: '75px',
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto',
          filter: 'drop-shadow(0 2px 6px rgb(247, 243, 243))'
        }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    ) : (
      <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', textAlign: 'center', py: 1 }}>
        {company?.name || 'Narevek CRM'}
      </Typography>
    )}

  </Box>
        
        {/* Close button on mobile */}
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            sx={{
              position: 'absolute', top: 6, right: 6,
              color: '#94a3b8', '&:hover': { color: '#fff' }
            }}
          >
            ✕
          </IconButton>
        )}
      </Box>

      {/* User pill */}
      <Box sx={{
        mx: 2, my: 1.5, px: 1.5, py: 1,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.05)',
        display: 'flex', alignItems: 'center', gap: 1.2
      }}>
        <Avatar sx={{
          width: 30, height: 30, fontSize: '0.75rem', fontWeight: 700,
          background: 'linear-gradient(135deg, #3b82f6, #0d9488)'
        }}>
          {initials(user?.name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#e2e8f0', lineHeight: 1.1 }} noWrap>
            {user?.name}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#64748b', textTransform: 'capitalize' }} noWrap>
            {user?.role}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2 }} />

      {/* Nav items */}
      <List sx={{ flexGrow: 1, mt: 0.5, px: 1 }}>
        {filteredMenuItems.map(item => (
          <ListItemButton
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            selected={isSelected(item.path)}
            sx={{
              borderRadius: 2,
              mb: 0.25,
              px: 1.5,
              py: 0.9,
              color: isSelected(item.path) ? '#fff' : '#94a3b8',
              background: isSelected(item.path)
                ? 'linear-gradient(90deg, rgba(59,130,246,0.35) 0%, rgba(13,148,136,0.2) 100%)'
                : 'transparent',
              borderLeft: isSelected(item.path) ? '3px solid #3b82f6' : '3px solid transparent',
              '&:hover': {
                background: 'rgba(255,255,255,0.07)',
                color: '#fff'
              },
              '&.Mui-selected': {
                background: 'linear-gradient(90deg, rgba(59,130,246,0.3) 0%, rgba(13,148,136,0.15) 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, rgba(59,130,246,0.4) 0%, rgba(13,148,136,0.25) 100%)'
                }
              },
              transition: 'all 0.15s ease'
            }}
          >
            <ListItemIcon sx={{
              minWidth: 36,
              color: isSelected(item.path) ? '#60a5fa' : '#64748b'
            }}>
              {item.path === '/follow-ups' && overdueFollowUps > 0 ? (
                <Badge badgeContent={overdueFollowUps} color="error" max={99}>
                  {item.icon}
                </Badge>
              ) : item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isSelected(item.path) ? 600 : 400 }}
            />
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2 }} />
      <Box sx={{ p: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: '#f87171',
            px: 1.5, py: 0.9,
            '&:hover': { background: 'rgba(239,68,68,0.1)', color: '#fca5a5' },
            transition: 'all 0.15s ease'
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: '#f87171' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.85rem' }} />
        </ListItemButton>
        {/* Quiet build marker — the only place this app's version is visible
            anywhere, on screen or over the API. Without it, confirming which
            release is actually live meant SSHing in and reading package.json
            by hand, which is exactly what this deploy needed to do. */}
        <Typography sx={{ fontSize: '0.65rem', color: '#475569', textAlign: 'center', mt: 0.5 }}>
          v{pkg.version}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'linear-gradient(90deg, #1e3a8a 0%, #1d4ed8 60%, #0d9488 100%)',
          boxShadow: '0 2px 12px rgba(30,58,138,0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { sm: 'none' },
              bgcolor: 'rgba(255,255,255,0.12)',
              borderRadius: 1.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
          >
            {isMobile ? 'Narevek CRM' : `Welcome, ${user?.name}`}
          </Typography>

          <NotificationBell />

          <Tooltip title={`${user?.name} · ${user?.role}`}>
            <Avatar
              sx={{
                ml: 1, width: 34, height: 34,
                bgcolor: 'rgba(255,255,255,0.18)',
                color: '#fff', fontWeight: 700, fontSize: 13,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' }
              }}
            >
              {initials(user?.name)}
            </Avatar>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Sidebar nav */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none'
            }
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop permanent drawer */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none'
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          overflow: 'hidden'
        }}
      >
        <Toolbar />
        <TrialBanner company={company} />
        {children}
      </Box>

      {/* Floating Chat Bubble */}
      <ChatBubble user={user} />
    </Box>
  );
}
